import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import draftToHtml from 'draftjs-to-html';
import { renderHashtag } from 'approot/shared/logic/render-hash-tag';

import { spacing4 } from 'approot/shared/styles/spacing.style';
import { Box } from 'approot/shared/styles/box.style';
import ContentPageRichtext from 'approot/shared/content-page/content-page-richtext';
import { learningSequenceTags } from 'webclient.constants';
import { JSONObject } from 'global/graphql/scalars.type';
import { hasRichtextContent } from 'lib/form/richtextarea/richtextarea.utils';
import * as Styled from './teaching-learning-sequence.style';

type CommonProps = {
  content?: JSONObject;
  title?: string;
};

type ViewProps = {
  isSmall?: boolean;
} & CommonProps;

type EditProps = {
  fieldName: string;
  onChange: (json: JSONObject) => void;
  placeholder?: string;
} & CommonProps;

type FormData = {
  [fieldName: string]: JSONObject;
};

export const TeachingLearningSequenceRichtextView = ({
  content,
  title,
  isSmall,
}: ViewProps) => {
  if (!content) return null;

  const contentMarkup = draftToHtml(
    content,
    undefined,
    undefined,
    renderHashtag
  );

  const hideRichtext = !hasRichtextContent(content);
  const hideTitle = hideRichtext || !title;

  return (
    <>
      {!hideTitle && (
        <Styled.TeachingLearningSequenceViewTitle marginBottom={spacing4}>
          {title}
        </Styled.TeachingLearningSequenceViewTitle>
      )}
      <Styled.TeachingLearningSequenceRichtext
        $isSmall={isSmall}
        dangerouslySetInnerHTML={{ __html: contentMarkup }}
      />
    </>
  );
};

export const TeachingLearningSequenceRichtextEdit = ({
  content,
  fieldName,
  placeholder,
  onChange,
  title,
}: EditProps) => {
  const [isLocalEditing, setIsLocalEditing] = useState<boolean>(false);

  const formMethods = useForm<FormData>({
    defaultValues: {
      [fieldName]: content,
    },
  });

  const { getValues } = formMethods;

  return (
    <Box $marginBottom={24}>
      {title && (
        <Styled.TeachingLearningSequenceLabel>
          {title}
        </Styled.TeachingLearningSequenceLabel>
      )}
      <FormProvider {...formMethods}>
        <form>
          <ContentPageRichtext
            isEditMode
            content={content}
            fieldName={fieldName}
            placeholder={placeholder}
            isSaving={false}
            isLocalEditing={isLocalEditing}
            hideSaveButton
            onBlur={() => onChange(getValues(fieldName))}
            setIsLocalEditing={() => setIsLocalEditing(true)}
            suggestions={learningSequenceTags}
          />
        </form>
      </FormProvider>
    </Box>
  );
};
